import React, { useState, useEffect, useRef } from 'react';
import { BsCheckCircleFill, BsClipboard, BsSlashCircle, BsShieldExclamation, BsArrowCounterclockwise } from 'react-icons/bs';
import ConfirmationModal from './ConfirmationModal';
import configDetails from '../Config/Config';
import logger from '../utils/Logger';

const COMPONENT_NAME = 'ShareModal';
const { baseUrl, PUBLIC_ACCESS } = configDetails;

// Auth headers — the owner token is only available to authenticated pages
const buildHeaders = (authIdToken) => ({
  'Content-Type': 'application/json',
  ...(authIdToken ? { 'Authorization': authIdToken } : {})
});

const ShareModal = ({ show, onClose, shareLink, conversationId, authIdToken, initialStatus, onStatusChange }) => {
  const [access, setAccess] = useState('public');
  const [copied, setCopied] = useState(false);

  // --- Public access (revoke) state ---
  const [sharingStatus, setSharingStatus] = useState(initialStatus || null); // latest GET /public_access/{id}
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusError, setStatusError] = useState(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [revokeMessage, setRevokeMessage] = useState(null);   // { type: 'success'|'error', text }
  const wasOpenRef = useRef(false);

  // Use the shareLink prop if provided, otherwise fallback to cookie (for safety)
  let link = shareLink || '';
  if (link && access) {
    // Add access param if not already present
    if (!link.includes('access=')) {
      link += `?access=${access}`;
    } else {
      // Replace access param if already present
      link = link.replace(/access=\w+/, `access=${access}`);
    }
  }

  const handleCopy = async () => {
    if (link) {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  // Fetch the CURRENT sharing status from the backend every time the popup opens.
  // We never rely on stale frontend state for revocation eligibility.
  const fetchSharingStatus = async () => {
    if (!conversationId) return;
    setLoadingStatus(true);
    setStatusError(null);
    try {
      const res = await fetch(`${baseUrl}${PUBLIC_ACCESS}/${conversationId}`, {
        method: 'GET',
        headers: buildHeaders(authIdToken)
      });
      if (!res.ok) throw new Error(`Failed to fetch sharing status (${res.status})`);
      const data = await res.json();
      setSharingStatus(data);
      if (typeof onStatusChange === 'function') onStatusChange(data);
      logger.info('Sharing status refreshed', {
        component: COMPONENT_NAME,
        action: 'FETCH_SHARING_STATUS',
        context: {
          conversationId,
          can_revoke: data?.can_revoke,
          public_access_revoked: data?.public_access_revoked,
          sharing_visibility: data?.sharing_visibility,
          latest_action_status: data?.latest_action_status
        }
      });
    } catch (error) {
      setStatusError('Unable to load sharing status.');
      logger.error('Failed to fetch sharing status', {
        component: COMPONENT_NAME,
        action: 'FETCH_SHARING_STATUS_ERROR',
        error,
        context: { conversationId }
      });
    } finally {
      setLoadingStatus(false);
    }
  };

  // Refresh status each time the owner opens the popup
  useEffect(() => {
    const opened = show && !wasOpenRef.current;
    wasOpenRef.current = show;
    if (opened && conversationId) {
      setRevokeMessage(null);
      setAccess('public');
      fetchSharingStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, conversationId]);

  // Revoke is offered only when the backend explicitly allows it
  const isRevoked = !!(sharingStatus && sharingStatus.public_access_revoked);
  const canRevoke = !!(
    sharingStatus &&
    sharingStatus.can_revoke === true &&
    sharingStatus.public_access_revoked !== true
  );

  // POST /public_access/{conversation_id}/revoke — only on explicit owner confirmation
  const handleRevokeConfirmed = async () => {
    if (revoking || !conversationId) return; // prevent duplicate submissions
    setRevoking(true);
    setRevokeMessage(null);
    try {
      const res = await fetch(`${baseUrl}${PUBLIC_ACCESS}/${conversationId}/revoke`, {
        method: 'POST',
        headers: buildHeaders(authIdToken)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.message || `Failed to revoke access (${res.status})`);
      }
      const merged = {
        ...(sharingStatus || {}),
        ...data,
        can_revoke: false,
        public_access_revoked: data.public_access_revoked !== undefined ? data.public_access_revoked : true
      };
      setSharingStatus(merged);
      setRevokeMessage({ type: 'success', text: data.message || 'Public access revoked successfully.' });
      if (typeof onStatusChange === 'function') onStatusChange(merged);
      logger.info('Public access revoked from share modal', {
        component: COMPONENT_NAME,
        action: 'REVOKE_PUBLIC_ACCESS_SUCCESS',
        context: { conversationId }
      });
    } catch (error) {
      setRevokeMessage({ type: 'error', text: error.message || 'Failed to revoke access.' });
      logger.error('Revoke public access failed', {
        component: COMPONENT_NAME,
        action: 'REVOKE_PUBLIC_ACCESS_ERROR',
        error,
        context: { conversationId }
      });
    } finally {
      setRevoking(false);
      setShowRevokeConfirm(false);
    }
  };

    // POST /public_access/{conversation_id}/restore
  const handleRestore = async () => {
    if (restoring || !conversationId) return;
    setRestoring(true);
    setRevokeMessage(null);
    try {
      const res = await fetch(`${baseUrl}${PUBLIC_ACCESS}/${conversationId}/restore`, {
        method: 'POST',
        headers: buildHeaders(authIdToken)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.error) {
        throw new Error(data.message || `Failed to restore public access (${res.status})`);
      }
      const merged = {
        ...(sharingStatus || {}),
        ...data,
        public_access_revoked: false,
        public_access_revoked_at: null
      };
      setSharingStatus(merged);
      setAccess('public');
      setRevokeMessage({ type: 'success', text: data.message || 'Public access restored successfully.' });
      if (typeof onStatusChange === 'function') onStatusChange(merged);
      logger.info('Public access restored from share modal', {
        component: COMPONENT_NAME,
        action: 'RESTORE_PUBLIC_ACCESS_SUCCESS',
        context: { conversationId }
      });
      fetchSharingStatus(); // re-sync can_revoke from backend so the Revoke button reappears
    } catch (error) {
      setRevokeMessage({ type: 'error', text: error.message || 'Failed to restore access.' });
      logger.error('Restore public access failed', {
        component: COMPONENT_NAME,
        action: 'RESTORE_PUBLIC_ACCESS_ERROR',
        error,
        context: { conversationId }
      });
    } finally {
      setRestoring(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.25)' }} tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Share Conversation</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label fw-bold">Access</label>
              <select
                className="form-select"
                value={access}
                onChange={e => setAccess(e.target.value)}
                style={{ maxWidth: 180 }}
              >
                <option value="public">🌐 Public</option>
                <option value="restricted">🔒 Restricted</option>
              </select>
            </div>
            <div className="input-group mb-2">
              <input
                type="text"
                className="form-control"
                value={link}
                readOnly
                style={{ fontFamily: 'monospace', fontSize: '0.95rem' }}
              />
              <button
                className={`btn ${copied ? 'btn-success' : 'btn-primary'} d-flex align-items-center`}
                onClick={handleCopy}
                type="button"
                disabled={!link || isRevoked}
              >
                {copied ? <BsCheckCircleFill className="me-2" /> : <BsClipboard className="me-2" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
            <div className="form-text">Share this link to allow others to view the conversation.</div>

            {/* ── Revoke Public Access (owner-confirmed action) ───────────────── */}
            {conversationId && (
              <div className="mt-3 pt-3 border-top">
                {loadingStatus && (
                  <div className="form-text mb-0">Checking sharing status…</div>
                )}
                {!loadingStatus && statusError && (
                  <div className="form-text text-danger mb-0">{statusError}</div>
                )}
                {!loadingStatus && !statusError && isRevoked && (
                  <>
                    <div
                      className="d-flex align-items-start gap-2 p-2 rounded-2"
                      style={{ background: 'rgba(220,53,69,0.08)', color: '#842029', fontSize: '0.9rem' }}
                      role="status"
                    >
                      <BsShieldExclamation className="mt-1" size={14} />
                      <span>Public access to this conversation has been revoked. Viewers with the link can no longer see it.</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-success d-flex align-items-center gap-2 mt-2"
                      onClick={handleRestore}
                      disabled={restoring}
                    >
                      <BsArrowCounterclockwise size={14} />
                      {restoring ? 'Restoring...' : 'Restore Public Access'}
                    </button>
                  </>
                )}
                {!loadingStatus && !statusError && !isRevoked && canRevoke && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-danger d-flex align-items-center gap-2"
                      onClick={() => { setRevokeMessage(null); setShowRevokeConfirm(true); }}
                      disabled={revoking}
                    >
                      <BsSlashCircle size={14} />
                      {revoking ? 'Revoking...' : 'Revoke Public Access'}
                    </button>
                    <div className="form-text mt-1">
                      Viewers with the public link will lose access. You keep full access to the transcript and summary.
                    </div>
                  </>
                )}
                {revokeMessage && (
                  <div
                    className={`mt-2 p-2 rounded-2 ${revokeMessage.type === 'success' ? '' : ''}`}
                    style={{
                      fontSize: '0.9rem',
                      background: revokeMessage.type === 'success' ? 'rgba(25,135,84,0.1)' : 'rgba(220,53,69,0.1)',
                      color: revokeMessage.type === 'success' ? '#0f5132' : '#842029'
                    }}
                    role="status"
                  >
                    {revokeMessage.text}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showRevokeConfirm}
        onConfirm={handleRevokeConfirmed}
        onCancel={() => setShowRevokeConfirm(false)}
        title="Revoke Public Access"
        message="Anyone using the public link will no longer be able to view this conversation. You will still be able to view the transcript and summary. This action cannot be undone."
        confirmText={revoking ? 'Revoking...' : 'Revoke'}
        cancelText="Cancel"
      />
    </div>
  );
};

export default ShareModal;
